import { FillOrderGroup } from "./FillOrderGroup";
import { FillOrderData, OrderSlippageService } from "shared/cubiq-services";
import { Subject } from "rxjs";
import { DetailFillCcyPair } from "../../../shared/cubiq-services/action/impl/DetailFillCcyPair";

export class FillOrdersModel {
  public activeIndex: number = 0;
  public groups: Array<FillOrderGroup> = [];
  public showReactivate: boolean;
  public canFill: boolean;

  public showFill: boolean;
  public showFillTP: boolean;
  public showFillSL: boolean;

  public menuItems: Array<any> = [];

  private pairGroupMap: Map<string, FillOrderGroup> = new Map();

  public fillActionSubject: Subject<string>;
  public modelEmptiedSubject: Subject<void>;
  public ownerDocument: Document;

  constructor(
    data: Array<DetailFillCcyPair>,
    private slippageService: OrderSlippageService,
  ) {
    this.fillActionSubject = new Subject();
    this.modelEmptiedSubject = new Subject<void>();
    data.forEach((element) => {
      let slippage = this.slippageService.getAutoSlippage(element.pairName);

      let group = new FillOrderGroup(
        element.pairName,
        element.rowData,
        element.minPrecision,
        element.maxPrecision,
        slippage,
      );

      this.groups.push(group);
      this.pairGroupMap.set(group.pairName, group);
    });
  }

  tabIndexChange(index: number): void {
    this.activeIndex = index;
    this.updateButtonFlags();
  }

  constructMenuItems(): void {
    this.menuItems = [];
    this.menuItems.push({ label: "Fill", command: this.menuClick.bind(this) });
    if (this.showFillTP) {
      this.menuItems.push({
        label: "Fill TP",
        command: this.menuClick.bind(this),
      });
    }
    if (this.showFillSL) {
      this.menuItems.push({
        label: "Fill SL",
        command: this.menuClick.bind(this),
      });
    }
  }

  menuClick(event) {
    this.fillActionSubject.next(event.item.label);
  }

  updateButtonFlags(): void {
    this.showFill = false;
    this.showReactivate = false;
    this.showFillTP = false;
    this.showFillSL = false;
    let currentGroup = this.groups[this.activeIndex];
    if (currentGroup) {
      if (currentGroup.selectedIds.length > 0) {
        this.showFill = true;
      }
      if (currentGroup.selectedTriggeredIds.length > 0 && 0 == currentGroup.selectedMKTIds.length) {
        this.showReactivate = true;
      }
      if (currentGroup.selectedTpIds.length > 0) {
        this.showFillTP = true;
      }
      if (currentGroup.selectedSlIds.length > 0) {
        this.showFillSL = true;
      }
    }
    this.constructMenuItems();
  }

  deleteGroupByIndex(idx: number) {
    this.groups.splice(idx, 1);
  }

  deleteRow(row: any) {}

  deleteRowById(id: string): boolean {
    let wasNotExpected: boolean = false;
    this.groups.forEach((group) => {
      if (group.deleteRowById(id)) {
        wasNotExpected = true;
      }
    });
    this.disposeBlankGroups();
    return wasNotExpected;
  }

  removeFromQueue(id: string): void {
    this.groups.forEach((group) => {
      if (group.removeFromQueue(id)) {
        return;
      }
    });
  }

  disposeBlankGroups(): void {
    for (let i = this.groups.length - 1; i >= 0; i--) {
      if (this.groups[i] && this.groups[i].ids.length == 0) {
        this.pairGroupMap.delete(this.groups[i].pairName);
        this.groups.splice(i, 1);
      }
    }
    if (this.getTotalCount() == 0) {
      this.modelEmptiedSubject.next();
    }
  }

  getTotalCount(): number {
    let ids = this.getAllTabIds();
    if (ids) return ids.length;
    return 0;
  }

  addRows(rows: DetailFillCcyPair[]) {
    rows.forEach((element) => {
      if (!this.pairGroupMap.get(element.pairName)) {
        let slippage = this.slippageService.getAutoSlippage(element.pairName);

        let group = new FillOrderGroup(
          element.pairName,
          element.rowData,
          element.minPrecision,
          element.maxPrecision,
          slippage,
        );

        this.groups.push(group);
        this.pairGroupMap.set(group.pairName, group);
      } else {
        let group = this.pairGroupMap.get(element.pairName);
        element.rowData.forEach((element) => {
          group.addRow(element);
        });
      }
    });
  }

  getSelectedFills(): Array<FillOrderData> {
    return this.groups[this.activeIndex].getSelectedFills(true);
  }

  getSelectedTPFills(): Array<FillOrderData> {
    let ret: Array<FillOrderData> = new Array<FillOrderData>();
    ret = ret.concat(this.groups[this.activeIndex].getSelectedTPFills(true));
    ret = ret.concat(this.groups[this.activeIndex].getSelectedMKTFills(true)); // Fill TP will act on MKT orders
    return ret;
  }

  getSelectedSLFills(): Array<FillOrderData> {
    return this.groups[this.activeIndex].getSelectedSLFills(true);
  }

  getSelectedTriggeredIds(): Array<string> {
    let trigIds = this.groups[this.activeIndex].getSelectedTriggerIdsToReactivate(true);
    this.updateButtonFlags();
    this.disposeBlankGroups();
    return trigIds;
  }

  getAllIds(): Array<string> {
    let ids = this.groups[this.activeIndex].getFillIdsToUnlock();
    this.disposeBlankGroups();
    return ids;
  }

  getGroupIds(idx: number): Array<string> {
    let ids = this.groups[idx].getFillIdsToUnlock();
    this.disposeBlankGroups();
    return ids;
  }

  getAllTabIdsToUnlock(): Array<string> {
    let ids: Array<string> = [];
    this.groups.forEach((element) => {
      ids = ids.concat(element.getFillIdsToUnlock());
    });
    this.disposeBlankGroups();
    return ids;
  }

  getAllTabIds(): Array<[string, string]> {
    let ids: Array<[string, string]> = [];
    this.groups.forEach((element) => {
      ids = ids.concat(element.ids.map((id, index) => [id, element.refs[index]]));
    });
    return ids;
  }

  setPositionBook(data): void {
    this.groups[this.activeIndex].setPositionBook(data);
  }

  setSlippage(value): void {
    this.groups[this.activeIndex].setSlippage(value);
  }
}
